import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { Profissional, TipoDeContato } from '@/data/tipos';
import {
    CamposDoFornecedor, CamposDoPrestador, formDoFornecedor, formDoPrestador, problemaDoPrestador,
    salvarFornecedorDoForm, salvarPrestadorDoForm, useApoioDoCadastro,
} from './CadastroDeTerceiros';

const ROTULOS: Record<TipoDeContato, { titulo: string; acao: string; descricao: string }> = {
    paciente: { titulo: 'Novo paciente', acao: 'Criar contato', descricao: 'Cadastre o contato e vincule ao profissional responsável.' },
    lead: { titulo: 'Novo lead', acao: 'Criar contato', descricao: 'Cadastre o contato e vincule ao responsável comercial.' },
    /* O e-mail é o que faz a conta criada pela pessoa cair nesta clínica — e é
       para ele que o convite sai, logo depois do cadastro. */
    profissional: {
        titulo: 'Cadastrar profissional', acao: 'Cadastrar profissional',
        descricao: 'O convite sai para este e-mail, e a pessoa entra criando a conta com ele.',
    },
    fornecedor: {
        titulo: 'Novo fornecedor', acao: 'Cadastrar fornecedor',
        descricao: 'O mesmo cadastro de Compras e Estoque: aparece nos pedidos e nas entradas.',
    },
    prestador: {
        titulo: 'Novo prestador de serviços', acao: 'Cadastrar prestador',
        descricao: 'Eletricista, pintor, manutenção de equipamento e outros serviços da clínica.',
    },
};

const TIPOS = [
    { value: 'paciente', label: 'Paciente' },
    { value: 'lead', label: 'Lead' },
    { value: 'profissional', label: 'Profissional' },
    { value: 'fornecedor', label: 'Fornecedor' },
    { value: 'prestador', label: 'Prestador de serviços' },
];

export interface NovoContatoModalProps {
    open: boolean;
    tipo: TipoDeContato;
    /** O nome que a pessoa já digitou na busca de onde veio. */
    nomeInicial?: string;
    onClose: () => void;
    /** Recebe o contato criado: quem cadastrou a partir de outra tela quer usá-lo ali. */
    onCriado: (criado: { id: string; nome: string }) => void;
}

export function NovoContatoModal({ open, tipo, nomeInicial, onClose, onCriado }: NovoContatoModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idTelefone = useId();
    const idTipo = useId();
    const idEmail = useId();
    const idCpf = useId();
    const idNascimento = useId();
    const idResponsavel = useId();
    const idFuncao = useId();
    const idRegistro = useId();

    const [nome, setNome] = useState('');
    const [telefone, setTelefone] = useState('');
    const [email, setEmail] = useState('');
    const [tipoEscolhido, setTipoEscolhido] = useState<TipoDeContato>(tipo);
    const [cpf, setCpf] = useState('');
    const [nascimento, setNascimento] = useState('');
    const [responsavelId, setResponsavelId] = useState('');
    const [funcao, setFuncao] = useState('');
    const [registro, setRegistro] = useState('');
    const [equipe, setEquipe] = useState<Profissional[] | null>(null);
    const [erroDaEquipe, setErroDaEquipe] = useState<string | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);
    const [fornecedor, setFornecedor] = useState(() => formDoFornecedor());
    const [prestador, setPrestador] = useState(() => formDoPrestador());
    const deTerceiro = tipoEscolhido === 'fornecedor' || tipoEscolhido === 'prestador';
    const apoio = useApoioDoCadastro(open && deTerceiro);

    /* Abrir é recomeçar, no tipo da aba de onde veio. */
    useEffect(() => {
        if (!open) return;
        setTipoEscolhido(tipo);
        setNome(nomeInicial ?? '');
        setTelefone(''); setEmail(''); setCpf(''); setNascimento(''); setResponsavelId('');
        setFuncao(''); setRegistro(''); setErro(null);
        setFornecedor({ ...formDoFornecedor(), nome: nomeInicial ?? '' });
        setPrestador({ ...formDoPrestador(), nome: nomeInicial ?? '' });

        let vivo = true;
        setEquipe(null);
        setErroDaEquipe(null);
        /* Responsável é quem está no cadastro, por id. A lista fixa ("Dra. Ana
           Prado") não batia com o nome da equipe, e o responsável era descartado. */
        dados().opcoesDoAgendamento()
            .then((o) => { if (vivo) setEquipe(o.profissionais); })
            .catch((e: unknown) => { if (vivo) setErroDaEquipe(e instanceof Error ? e.message : 'Não foi possível ler a equipe.'); });
        return () => { vivo = false; };
    }, [open, tipo, nomeInicial]);

    const rotulos = ROTULOS[tipoEscolhido];
    const ehProfissional = tipoEscolhido === 'profissional';
    const sujo = tipoEscolhido === 'fornecedor'
        ? JSON.stringify(fornecedor) !== JSON.stringify({ ...formDoFornecedor(), nome: nomeInicial ?? '' })
        : tipoEscolhido === 'prestador'
            ? JSON.stringify(prestador) !== JSON.stringify({ ...formDoPrestador(), nome: nomeInicial ?? '' })
            : nome.trim() !== '' && nome.trim() !== (nomeInicial ?? '').trim() || telefone.trim() !== '' || email.trim() !== '';

    /* Fornecedor e prestador não são contato de paciente: têm cadastro próprio (0159). */
    const criarTerceiro = async (): Promise<boolean> => {
        const problema = tipoEscolhido === 'fornecedor'
            ? (fornecedor.nome.trim() ? null : 'Dê a razão social do fornecedor.')
            : problemaDoPrestador(prestador);
        if (problema) {
            setErro(problema);
            return false;
        }
        setSalvando(true);
        try {
            const criado = tipoEscolhido === 'fornecedor'
                ? await salvarFornecedorDoForm(fornecedor)
                : await salvarPrestadorDoForm(prestador);
            aviso({ title: tipoEscolhido === 'fornecedor' ? 'Fornecedor cadastrado' : 'Prestador cadastrado', description: criado.nome });
            onCriado({ id: criado.id, nome: criado.nome });
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível cadastrar.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const criar = async (): Promise<boolean> => {
        if (deTerceiro) return criarTerceiro();
        if (!nome.trim() || (!ehProfissional && !telefone.trim())) {
            setErro('Nome e telefone são obrigatórios.');
            return false;
        }
        if (ehProfissional && !email.trim()) {
            setErro('O e-mail é obrigatório: é com ele que a pessoa cria a conta e entra.');
            return false;
        }
        setSalvando(true);
        try {
            const criado = await dados().criarContato({
                tipo: tipoEscolhido,
                nome: nome.trim(),
                telefone: telefone.trim(),
                ...(email.trim() ? { email: email.trim() } : {}),
                /* CPF e nascimento são da ficha do PACIENTE. Pedidos para lead,
                   eram descartados sem aviso — lead não tem ficha. */
                ...(tipoEscolhido === 'paciente' && cpf.trim() ? { cpf: cpf.trim() } : {}),
                ...(tipoEscolhido === 'paciente' && nascimento ? { nascimento } : {}),
                ...(!ehProfissional && responsavelId ? { profissionalId: responsavelId } : {}),
                ...(ehProfissional && funcao.trim() ? { especialidades: funcao.trim() } : {}),
                ...(ehProfissional && registro.trim() ? { registro: registro.trim() } : {}),
            });
            if (ehProfissional) {
                /* O cadastro já está gravado: convite que falha não o desfaz, e o
                   aviso diz que falta reenviar em vez de fingir que saiu. */
                try {
                    const convite = await dados().convidarMembro(criado.id);
                    aviso({
                        title: 'Profissional cadastrado',
                        description: convite.email
                            ? `Convite enviado para ${convite.email}.`
                            : `${nome.trim()} · na demonstração, nenhum e-mail sai`,
                    });
                } catch (e) {
                    aviso({
                        title: 'Profissional cadastrado, convite não saiu',
                        description: `${e instanceof Error ? e.message : 'Falha ao convidar.'} Reenvie pelo detalhe de ${nome.trim()}.`,
                        tone: 'warning',
                    });
                }
            } else {
                aviso({ title: 'Contato criado', description: `${nome.trim()} · cadastro salvo` });
            }
            const nomeCriado = nome.trim();
            setNome(''); setTelefone(''); setEmail(''); setCpf(''); setNascimento(''); setErro(null);
            onCriado({ id: criado.id, nome: nomeCriado });
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível criar o contato.');
            return false;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={open} onClose={onClose} width={520} title={rotulos.titulo} description={rotulos.descricao}
            dirty={sujo} onSave={criar} saveLabel={rotulos.acao}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void criar()} loading={salvando}>{rotulos.acao}</Button>
                </>
            }
        >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {/* Sempre o primeiro, e o mesmo elemento: trocar o tipo não tira o foco dele. */}
                <FormField label="Tipo" htmlFor={idTipo} style={{ gridColumn: '1 / -1' }}>
                    <Select id={idTipo} value={tipoEscolhido} onChange={(v) => { setTipoEscolhido(v as TipoDeContato); setErro(null); }} options={TIPOS} />
                </FormField>
                {deTerceiro ? (
                    <>
                        {(erro || apoio.erro) && (
                            <div style={{ gridColumn: '1 / -1' }}>
                                <Alert tone="danger" dismissible={false}>{erro ?? apoio.erro}</Alert>
                            </div>
                        )}
                        {tipoEscolhido === 'fornecedor' ? (
                            <CamposDoFornecedor
                                form={fornecedor} comAtivo={false} categorias={apoio.categorias}
                                onChange={(m) => { setFornecedor((f) => ({ ...f, ...m })); setErro(null); }}
                            />
                        ) : (
                            <CamposDoPrestador
                                form={prestador} comAtivo={false} apoio={apoio}
                                onChange={(m) => { setPrestador((f) => ({ ...f, ...m })); setErro(null); }}
                            />
                        )}
                    </>
                ) : (<>
                {erroDaEquipe && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <Alert tone="warning" title="A equipe não carregou" dismissible={false}>{erroDaEquipe}</Alert>
                    </div>
                )}
                <FormField label="Nome completo" required htmlFor={idNome} error={erro ?? undefined} style={{ gridColumn: '1 / -1' }}>
                    <Input id={idNome} value={nome} onChange={(v) => { setNome(v); setErro(null); }} placeholder="Nome do contato" invalid={!!erro} />
                </FormField>
                <FormField label="Telefone" required={!ehProfissional} htmlFor={idTelefone} style={{ gridColumn: '1 / -1' }}>
                    <Input id={idTelefone} type="tel" value={telefone} onChange={(v) => { setTelefone(v); setErro(null); }} placeholder="(11) 99999-9999" invalid={!!erro && !ehProfissional} />
                </FormField>
                <FormField label="E-mail" required={ehProfissional} htmlFor={idEmail} style={{ gridColumn: '1 / -1' }}
                    hint={ehProfissional ? 'Com este e-mail a pessoa cria a conta e cai nesta clínica' : undefined}
                >
                    <Input id={idEmail} type="email" value={email} onChange={(v) => { setEmail(v); setErro(null); }} placeholder="nome@email.com" />
                </FormField>
                {tipoEscolhido === 'paciente' && (
                    <>
                        <FormField label="CPF" htmlFor={idCpf}><Input id={idCpf} value={cpf} onChange={setCpf} placeholder="000.000.000-00" /></FormField>
                        <FormField label="Nascimento" htmlFor={idNascimento}><Input id={idNascimento} type="date" value={nascimento} onChange={setNascimento} /></FormField>
                    </>
                )}
                {ehProfissional ? (
                    <>
                        <FormField label="Função" htmlFor={idFuncao}><Input id={idFuncao} value={funcao} onChange={setFuncao} placeholder="Dermatologista" /></FormField>
                        <FormField label="Registro no conselho" htmlFor={idRegistro}><Input id={idRegistro} value={registro} onChange={setRegistro} placeholder="CRM-SP 000.000" /></FormField>
                    </>
                ) : (
                    <FormField label={tipoEscolhido === 'lead' ? 'Responsável' : 'Profissional responsável'} htmlFor={idResponsavel} style={{ gridColumn: '1 / -1' }}>
                        <Select
                            id={idResponsavel} value={responsavelId} onChange={setResponsavelId} placeholder="Sem responsável"
                            disabled={!equipe} options={(equipe ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                        />
                    </FormField>
                )}
                </>)}
            </div>
        </Modal>
    );
}
